import React, { useEffect, useMemo, useState } from "react";

import { Search, X } from "lucide-react";

import { AccountBubbleDriftChevron } from "./AccountBubbleDriftChevron";
import { resolveAccountBubbleRole } from "./portfolioBubbleRole";
import {
  formatAggregateRl,
  formatLevel,
  getPrototypeAlBubbleClass,
  getInitials,
  isUnassessedLevelLabel,
  UNASSESSED_LEVEL_TITLE,
  unassessedLevelTitle,
} from "../account-relationships/relationshipUtils";

export function PortfolioCellAccountPickerModal({
  open,
  accounts,
  alRow,
  rlColumn,
  onClose,
  onSelectAccount,
  gridSearchQuery = "",
  forceExecutiveRing = false,
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    // Carry the grid's search term into the modal so the list is pre-filtered.
    setQuery(gridSearchQuery || "");
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, gridSearchQuery]);

  const displayAccounts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(account =>
      String(account.name || "")
        .toLowerCase()
        .includes(q)
    );
  }, [accounts, query]);

  if (!open || !accounts?.length) return null;

  const alLabel = alRow === "unknown" ? "AL ?" : formatLevel("AL", alRow);
  const rlLabel = rlColumn === "unknown" ? "RL ?" : formatLevel("RL", rlColumn);

  return (
    <div
      role="presentation"
      className="modal-bg open"
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <article
        className="modal pg-cell-picker-modal"
        role="dialog"
        aria-labelledby="portfolio-cell-picker-title"
        style={{ maxWidth: 420 }}
        onClick={e => e.stopPropagation()}
      >
        <header className="modal-hdr">
          <div className="mh-info">
            <h2 id="portfolio-cell-picker-title" className="modal-title">
              Select account
            </h2>
            <div className="modal-sub">
              {accounts.length} accounts ·{" "}
              <span title={unassessedLevelTitle(alLabel)}>{alLabel}</span>
              {" × "}
              <span title={unassessedLevelTitle(rlLabel)}>{rlLabel}</span>
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="modal-body">
          {accounts.length > 6 || query ? (
            <div style={{ position: "relative", marginBottom: 12 }}>
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <input
                type="search"
                className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm"
                placeholder="Search accounts…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoFocus
              />
            </div>
          ) : null}

          {!displayAccounts.length ? (
            <p className="pg-cell-picker-empty">
              No accounts match your search.
            </p>
          ) : (
            <ul
              className="pg-cell-picker-list"
              role="listbox"
              aria-label="Accounts in this cell"
            >
              {displayAccounts.map(account => {
                const al =
                  account.alLevel === null || account.alLevel === undefined
                    ? "AL ?"
                    : formatLevel("AL", account.alLevel);
                const rl =
                  account.rlAggregate === null ||
                  account.rlAggregate === undefined
                    ? "RL ?"
                    : formatAggregateRl(account.rlAggregate);
                return (
                  <li key={account.id}>
                    <button
                      type="button"
                      className="pg-cell-picker-row"
                      role="option"
                      onClick={() => {
                        onSelectAccount(account);
                        onClose();
                      }}
                    >
                      <span
                        className={[
                          "acct-bubble",
                          getPrototypeAlBubbleClass(account.alLevel),
                          resolveAccountBubbleRole(account, {
                            forceExecutiveRing,
                          }).strokeClass,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={{
                          width: 36,
                          height: 36,
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                        aria-hidden
                      >
                        <span className="ab-init" style={{ fontSize: 11 }}>
                          {getInitials(account.name)}
                        </span>
                        <AccountBubbleDriftChevron
                          rlAggregate={account.rlAggregate}
                        />
                      </span>
                      <span className="pg-cell-picker-row-info">
                        <span className="pg-cell-picker-row-name">
                          {account.name}
                        </span>
                        <span
                          className="pg-cell-picker-row-meta"
                          title={
                            isUnassessedLevelLabel(al) ||
                            isUnassessedLevelLabel(rl)
                              ? UNASSESSED_LEVEL_TITLE
                              : undefined
                          }
                        >
                          <span title={unassessedLevelTitle(al)}>{al}</span>
                          {" · "}
                          <span title={unassessedLevelTitle(rl)}>{rl}</span>
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </article>
    </div>
  );
}

export default PortfolioCellAccountPickerModal;
